// ---------------------------------------------------------------------------
// ClientOverviewContext — React Context + useReducer for overview UI state
// Persists characteristicsExpanded and activeViewId in sessionStorage
// ---------------------------------------------------------------------------

import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
} from "react";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface ClientOverviewState {
  characteristicsExpanded: boolean;
  activeViewId: string;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ClientOverviewAction =
  | { type: "TOGGLE_CHARACTERISTICS" }
  | { type: "SET_ACTIVE_VIEW"; payload: string }
  | { type: "RESTORE_STATE"; payload: Partial<ClientOverviewState> };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_STATE: ClientOverviewState = {
  characteristicsExpanded: true,
  activeViewId: "Investor View Default",
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function clientOverviewReducer(
  state: ClientOverviewState,
  action: ClientOverviewAction
): ClientOverviewState {
  switch (action.type) {
    case "TOGGLE_CHARACTERISTICS":
      return {
        ...state,
        characteristicsExpanded: !state.characteristicsExpanded,
      };
    case "SET_ACTIVE_VIEW":
      return {
        ...state,
        activeViewId: action.payload,
      };
    case "RESTORE_STATE":
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Persistence helpers
// Persists each top-level field as its own sessionStorage key so tests
// and external code can read individual values directly.
// ---------------------------------------------------------------------------

function persistState(state: ClientOverviewState): void {
  try {
    sessionStorage.setItem("characteristicsExpanded", String(state.characteristicsExpanded));
    sessionStorage.setItem("activeViewId", state.activeViewId);
  } catch {
    // Silently fail if sessionStorage is not available (e.g., SSR)
  }
}

function restoreState(): Partial<ClientOverviewState> | null {
  try {
    const expanded = sessionStorage.getItem("characteristicsExpanded");
    const viewId = sessionStorage.getItem("activeViewId");
    const restored: Partial<ClientOverviewState> = {};
    if (expanded !== null) {
      restored.characteristicsExpanded = expanded === "true";
    }
    if (viewId !== null) {
      restored.activeViewId = viewId;
    }
    return Object.keys(restored).length > 0 ? restored : null;
  } catch {
    // Silently fail
  }
  return null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ClientOverviewContextValue {
  state: ClientOverviewState;
  dispatch: React.Dispatch<ClientOverviewAction>;
}

const ClientOverviewContext = createContext<ClientOverviewContextValue | null>(
  null
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ClientOverviewProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [state, dispatch] = useReducer(
    clientOverviewReducer,
    DEFAULT_STATE,
    (initial) => {
      const stored = restoreState();
      return stored ? { ...initial, ...stored } : initial;
    }
  );

  // Persist state changes to sessionStorage
  useEffect(() => {
    persistState(state);
  }, [state]);

  return (
    <ClientOverviewContext.Provider value={{ state, dispatch }}>
      {children}
    </ClientOverviewContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the current ClientOverviewState.
 * Must be used within a ClientOverviewProvider.
 */
export function useClientOverviewContext(): ClientOverviewState {
  const context = useContext(ClientOverviewContext);
  if (!context) {
    throw new Error(
      "useClientOverviewContext must be used within a ClientOverviewProvider"
    );
  }
  return context.state;
}

/**
 * Returns the dispatch function for ClientOverviewActions.
 * Must be used within a ClientOverviewProvider.
 */
export function useClientOverviewDispatch(): React.Dispatch<ClientOverviewAction> {
  const context = useContext(ClientOverviewContext);
  if (!context) {
    throw new Error(
      "useClientOverviewDispatch must be used within a ClientOverviewProvider"
    );
  }
  return context.dispatch;
}
